/**
 * Compatibility-form URL search writes (D3).
 *
 * Omitted `tax` and `tax=sector` are the same dashboard page. Every hop and
 * every in-place write must produce the shorter form — otherwise the first
 * click in a plain session pins taxonomy state the URL never asked for.
 * `validateSearch` re-hydrates the default on READ; this module is the WRITE
 * half. Dashboard in-place writes also run `stripSearchParams` after Zod so a
 * forgotten updater cannot pin `tax=sector` into the query string.
 */
import { defaultStringifySearch } from '@tanstack/react-router';

import {
  BOOK_FILTER_ON,
  TREEMAP_EXPAND_ON,
  formatCompareSet,
  type AreaId,
  type BookFilterParam,
  type CompareSet,
  type DashboardSearchParams,
  type FilterKey,
  type Taxonomy,
  type WindowKey,
} from '@meridian/contracts';

export interface NavSearchInput {
  readonly win: WindowKey;
  readonly idx: FilterKey;
  /* `| undefined` is required: route `Pick`s of optional URL fields arrive as
     present-and-undefined under exactOptionalPropertyTypes. */
  readonly tax?: Taxonomy | undefined;
  readonly area?: AreaId | undefined;
  readonly book?: BookFilterParam | undefined;
  readonly cmp?: CompareSet | undefined;
}

export interface WrittenNavSearch {
  readonly win: WindowKey;
  readonly idx: FilterKey;
  readonly tax?: 'industry';
  readonly area?: AreaId;
  readonly book?: typeof BOOK_FILTER_ON;
  readonly cmp?: CompareSet;
}

/**
 * Tax is written only when industry. Area rides along only then — an area
 * without industry would invent a parent crumb on a GICS session (AD-9).
 */
export function writtenNavSearch(input: NavSearchInput): WrittenNavSearch {
  const book = input.book === BOOK_FILTER_ON ? { book: BOOK_FILTER_ON } : {};
  const cmp = input.cmp === undefined ? {} : { cmp: input.cmp };
  const base = { win: input.win, idx: input.idx, ...book, ...cmp };
  if (input.tax !== 'industry') return base;
  if (input.area === undefined) return { ...base, tax: 'industry' };
  return { ...base, tax: 'industry', area: input.area };
}

export type DashboardSearchWrite = Omit<DashboardSearchParams, 'tax' | 'exp'> & {
  readonly tax?: 'industry';
  readonly exp?: typeof TREEMAP_EXPAND_ON;
};

/**
 * Drop `tax=sector` and collapsed `exp` so a dashboard write cannot pin an
 * omitted default. Expanded overlay is `exp=1` only — never `exp=0`.
 */
export function dashboardSearchWrite(parsed: DashboardSearchParams): DashboardSearchWrite {
  const { tax, exp, ...rest } = parsed;
  const withTax: DashboardSearchWrite = tax === 'industry' ? { ...rest, tax: 'industry' } : rest;
  if (exp === TREEMAP_EXPAND_ON) {
    return { ...withTax, exp: TREEMAP_EXPAND_ON };
  }
  return withTax;
}

export function hrefSearch(
  path: string,
  search: Readonly<Record<string, string | undefined>>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value !== undefined) params.set(key, value);
  }
  const qs = params.toString();
  return qs.length === 0 ? path : `${path}?${qs}`;
}

/** Typed query-string builder for a written hop. */
export function hrefForSearch(path: string, search: WrittenNavSearch): string {
  return hrefSearch(path, {
    win: search.win,
    idx: search.idx,
    ...(search.tax === undefined ? {} : { tax: search.tax }),
    ...(search.area === undefined ? {} : { area: search.area }),
    ...(search.book === undefined ? {} : { book: String(search.book) }),
    ...(search.cmp === undefined ? {} : { cmp: formatCompareSet(search.cmp) }),
  });
}

/**
 * Router write path. TanStack's default JSON-encodes arrays, which would turn
 * a compare set into `cmp=["AAPL","MSFT"]`. One comma-joined token is the
 * shareable form (`cmp=AAPL,MSFT`) and the only form `compareSetInput` reads.
 */
export function stringifyNavSearch(search: Record<string, unknown>): string {
  const cmp = search['cmp'];
  if (!Array.isArray(cmp)) return defaultStringifySearch(search);
  return defaultStringifySearch({ ...search, cmp: formatCompareSet(cmp as CompareSet) });
}
